import type { CSSProperties, ReactNode } from "react";

import type { ClubKit } from "@legacy-manager/engine";

import { Bandeira } from "../Bandeira";
import { PositionChip, StrengthBadge } from "../atoms";
import { contrasteCom } from "../brasoes/EditorDeUniforme";
import { AvatarDoJogador } from "./Avatar";

/**
 * O jogador como figurinha de álbum.
 *
 * O enquadramento é do peito para cima, como numa figurinha de copa: é ali que
 * está o rosto, que é o que distingue um jogador do outro. De corpo inteiro o
 * rosto ficava com trinta pixels numa coluna de trezentos, e onze fichas do
 * mesmo elenco pareciam a mesma.
 *
 * **O clube está na moldura, e não na camisa.** A foto vem com uniforme branco e
 * vai para a tela sem ser tocada: a versão que detectava o pano para trocar a
 * cor dele trazia um defeito novo a cada avatar novo, todos pela mesma causa,
 * que é adivinhar onde o pano acaba numa arte que não foi feita para ser
 * recortada. Figurinha de seleção resolve isso do mesmo jeito há cinquenta anos,
 * pondo as cores em volta da foto.
 *
 * **O escudo vai sem a sigla dentro dele, e a sigla vai embaixo.** Escrita por
 * cima, ela tapa o desenho: quem criou o clube pôs um leão ali e via três
 * letras. Embaixo, as duas coisas aparecem, e o escudo do canto vira o desenho
 * que ele é. O mesmo corte já existia no escudo grande do criador de clube.
 *
 * **A figurinha carrega os dados, como a ficha do campo da tática.** Bandeira,
 * posição, nome e força no rodapé, e o que mais couber sobre a foto chega por
 * `children`: a ficha põe ali moral, físico e os números da temporada. Antes o
 * nome e esses números ficavam embaixo da figurinha, repetindo posição e força,
 * e a ficha passava da altura da tela.
 */

export function FigurinhaDoJogador({
  playerId,
  nacionalidade,
  uniforme,
  escudo,
  sigla,
  numero,
  posicao,
  forca,
  nome,
  subtitulo,
  children,
}: {
  playerId: string;
  /** A sigla do país do jogador, que decide entre quais avatares sortear. */
  nacionalidade?: string;
  uniforme: ClubKit;
  /** O escudo do clube, no tamanho e com ou sem a sigla que a figurinha pedir. */
  escudo?: (size: number, semSigla?: boolean) => ReactNode;
  /** A sigla do clube, escrita embaixo do escudo. */
  sigla?: string;
  /** O número da camisa, quando o jogador tem um. */
  numero?: number | null;
  posicao: string;
  forca: number;
  /** O nome, no rodapé entre a posição e a força. */
  nome?: string;
  /** Idade, pé e o que mais couber numa linha, embaixo do nome. */
  subtitulo?: string;
  /** O que fica por cima da foto. */
  children?: ReactNode;
}): ReactNode {
  /*
   * A tinta do rodapé sai da cor do clube, e não é branca fixa.
   *
   * Metade dos clubes tem cor clara, e sobre ela um texto branco some. Preto ou
   * branco, o que se enxergar: é a mesma conta que o editor de uniforme usa para
   * a figura do peito.
   */
  const estilo = {
    "--figurinha-cor": uniforme.primary,
    "--figurinha-linha": uniforme.secondary,
    "--figurinha-tinta": contrasteCom(uniforme.primary),
  } as CSSProperties;

  return (
    <div className="figurinha" style={estilo}>
      {/*
        O número atrás do jogador, grande e apagado.

        É marca d'água e não informação: quem quer saber o número lê na tabela do
        elenco. Aqui ele preenche o vazio que sobra ao lado da cabeça e dá à
        figurinha a cara de material esportivo, que é o que ela imita.
      */}
      {numero !== null && numero !== undefined && (
        <span className="figurinha-numero">{numero}</span>
      )}

      {escudo && (
        <span className="figurinha-escudo">
          {escudo(42, true)}
          {sigla && <span className="figurinha-sigla">{sigla}</span>}
        </span>
      )}

      <AvatarDoJogador playerId={playerId} nacionalidade={nacionalidade} />

      {children}

      <div className="figurinha-rodape">
        {nacionalidade && <Bandeira codigo={nacionalidade} />}
        <PositionChip pos={posicao} />
        {nome && (
          <span className="figurinha-nome">
            <b>{nome}</b>
            {subtitulo && <small>{subtitulo}</small>}
          </span>
        )}
        <span style={{ marginLeft: "auto", fontSize: nome ? 22 : 17 }}>
          <StrengthBadge value={forca} />
        </span>
      </div>
    </div>
  );
}
